import { Link, useLocation } from "react-router-dom";
import {
  ArrowRight,
  BookOpen,
  Briefcase,
  Calendar,
  ChevronDown,
  ChevronRight,
  FolderKanban,
  GraduationCap,
  Menu,
  MessageSquareQuote,
  Newspaper,
  ShieldCheck,
  Star,
  User,
  X,
  ImageOff,
} from "lucide-react";
import React, { useEffect, useEffectEvent, useState } from "react";
import { logo } from "../image";
import { http } from "../../axios/axios";
import { MegaMenu, MenuCard, NavItem, resourceItems } from "../NavbarComponent";
import MainHeaderPage from "./MainHeaderPage";
import { GetImageUrl } from "../../helpers/GetImageUrl";

function MenuThumbnail({ image, size = "desktop" }) {
  const [failed, setFailed] = useState(false);
  const imageUrl = image ? GetImageUrl(image) : "";
  const dimensions = size === "mobile" ? "h-10 w-10" : "h-12 w-12";

  return (
    <span
      className={`${dimensions} flex shrink-0 items-center justify-center overflow-hidden rounded-md bg-slate-100 text-slate-400`}
      aria-hidden="true"
    >
      {imageUrl && !failed ? (
        <img
          src={imageUrl}
          alt=""
          loading="lazy"
          decoding="async"
          className="h-full w-full object-cover"
          onError={() => setFailed(true)}
        />
      ) : (
        <ImageOff size={18} />
      )}
    </span>
  );
}

function Head() {
  const location = useLocation();

  const [mobileOpen, setMobileOpen] = useState(false);
  const [openMenu, setOpenMenu] = useState(null);

  const [catalogMenuData, setCatalogMenuData] = useState([]);
  const [ipNetworkMenuData, setIpNetworkMenuData] = useState([]);
  const [solutionsMenuData, setSolutionsMenuData] = useState([]);
  const [servicesMenuData, setServicesMenuData] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(0);
  const [mobileSection, setMobileSection] = useState(null);
  const [mobileCategory, setMobileCategory] = useState(null);
  const [selectedService, setSelectedService] = useState(0);
  const [selectedSolution, setSelectedSolution] = useState(0);

  const navItemClass = `
  flex
  items-center
  justify-center
  gap-1
  shrink-0
  whitespace-nowrap
  text-center
  font-bold
  uppercase
  transition-colors
  hover:text-blue-600
  min-[900px]:text-[9px]
  min-[1000px]:text-[10px]
  min-[1190px]:text-[11px]
  min-[1280px]:text-xs
  min-[900px]:px-0.5
  min-[1190px]:px-1
`;

  const closeMobileMenu = () => {
    setMobileOpen(false);
    setMobileSection(null);
    setMobileCategory(null);
  };

  const closeAllMenus = () => {
    setOpenMenu(null);
    closeMobileMenu();
  };

  const resetMenusOnNavigation = useEffectEvent(() => {
    setOpenMenu(null);
    setMobileOpen(false);
    setMobileSection(null);
    setMobileCategory(null);
  });

  useEffect(() => {
    const fetchMenu = async () => {
      try {
        const res = await http.get("/navbar");

        // console.log("NAVBAR RESPONSE:", res.data?.data?.catalog);
        // console.log("SERVICES MENU:", res.data?.data?.services);
        // console.log("SOLUTIONS MENU:", res.data?.data?.solutions);

        const sortedCatalog = [...(res.data?.data?.catalog || [])]
          .sort((a, b) => (a.menuOrder ?? 9999) - (b.menuOrder ?? 9999))
          .map((category) => ({
            ...category,

            subCategories: [...(category.subCategories || [])].sort(
              (a, b) => (a.menuOrder ?? 9999) - (b.menuOrder ?? 9999),
            ),
          }));

        // console.log("SORTED CATALOG:", sortedCatalog);

        const sortedIpNetworks = [...(res.data?.data?.ipNetworks || [])]
          .sort((a, b) => (a.displayOrder ?? 9999) - (b.displayOrder ?? 9999))
          .map((category) => ({
            ...category,
            subCategories: [...(category.subCategories || [])].sort(
              (a, b) => (a.menuOrder ?? 9999) - (b.menuOrder ?? 9999),
            ),
          }));

        setCatalogMenuData(sortedCatalog);
        setIpNetworkMenuData(sortedIpNetworks);
        setServicesMenuData(res.data?.data?.services || []);
        setSolutionsMenuData(res.data?.data?.solutions || []);

        setSelectedCategory(0);
      } catch (err) {
        console.error("Navbar fetch error:", err);
      }
    };

    fetchMenu();
  }, [location.pathname]);

  useEffect(() => {
    const resetFrame = requestAnimationFrame(resetMenusOnNavigation);

    return () => cancelAnimationFrame(resetFrame);
  }, [location.pathname]);

  return (
    <>
      <header className="sticky top-0 z-50 bg-white border-b border-slate-200 shadow-sm relative">
        <div className=" px-4 lg:px-6">
          <div className="h-20 flex items-center justify-between">
            {
              <Link
                to="/"
                onClick={() => {
                  window.scrollTo({
                    top: 0,
                    left: 0,
                    behavior: "smooth",
                  });
                }}
              >
                <img
                  src={logo}
                  alt="Logo"
                  className="h-16 object-contain cursor-pointer"
                />
              </Link>
            }

            {/* <nav className="hidden px-7 lg:flex items-center gap-8"> */}
            <nav
              className="
    hidden min-[900px]:flex
    items-center
    justify-center
    w-full
    px-2 min-[1280px]:px-4 2xl:px-7
    min-[900px]:gap-1.5
    min-[1000px]:gap-2
    min-[1190px]:gap-3
    min-[1280px]:gap-4
    2xl:gap-6
  "
            >
              <NavItem
                title="Home"
                to="/"
                menu="home"
                setOpenMenu={setOpenMenu}
                className={navItemClass}
              />

              <NavItem
                title="About"
                to="/about"
                menu="about"
                setOpenMenu={setOpenMenu}
                className={navItemClass}
              />

              <NavItem
                title="Security Products"
                to="/security-product"
                menu="products"
                setOpenMenu={setOpenMenu}
                className={navItemClass}
              />

              <div
                className="relative shrink-0"
                onMouseEnter={() => {
                  setOpenMenu("catalog");
                  setSelectedCategory(0);
                }}
              >
                <button className={navItemClass}>
                  <span>Security Product Catalog</span>

                  <ChevronDown
                    size={14}
                    className={`shrink-0 transition-transform duration-200 ${
                      openMenu === "catalog" ? "rotate-180" : ""
                    }`}
                  />
                </button>
                {openMenu === "catalog" && (
                  <div
                    className="fixed left-0 top-30 w-screen mt-1 z-50"
                    onMouseEnter={() => setOpenMenu("catalog")}
                    onMouseLeave={() => setOpenMenu(null)}
                  >
                    <div className="bg-white border border-slate-100 shadow-lg flex">
                      <div
                        className="w-100 bg-slate-100/90 border-r border-slate-200
  max-h-[50vh] lg:max-h-[45vh] xl:max-h-[40vh]
  min-h-[50vh] lg:min-h-[45vh] xl:min-h-[40vh]
  overflow-y-auto scrollbar-none"
                      >
                        {catalogMenuData.map((category, index) => (
                          <div
                            key={category?._id}
                            onMouseEnter={() => setSelectedCategory(index)}
                            className={`mx-2 my-1 rounded-lg flex items-center justify-between px-5 py-4 text-xs font-bold uppercase cursor-pointer transition-all duration-300
                               ${
                                 selectedCategory === index
                                   ? "bg-slate-100 text-slate-900 border-l-4 border-blue-600"
                                   : "text-slate-600 hover:bg-slate-50 hover:text-slate-900"
                               }`}
                          >
                            <span className="flex-1 min-w-0 truncate">
                              {category?.name}
                            </span>
                            <ChevronRight
                              size={16}
                              className={`ml-3 transition-all duration-300 ${
                                selectedCategory === index
                                  ? "text-blue-600 translate-x-1"
                                  : "text-slate-400"
                              }`}
                            />
                          </div>
                        ))}
                      </div>

                      <div
                        className="flex-1 px-5 py-4
                                      max-h-[50vh] lg:max-h-[45vh] xl:max-h-[40vh]
                                      min-h-[50vh] lg:min-h-[45vh] xl:min-h-[40vh]
                                      overflow-y-auto scrollbar-none"
                      >
                        <div className="grid grid-cols-2 xl:grid-cols-4 gap-4">
                          {catalogMenuData[
                            selectedCategory
                          ]?.subCategories?.map((sub) => (
                            <Link
                              onClick={closeAllMenus}
                              key={sub._id}
                              to={`/security-product/sub-category/${sub.slug}`}
                              className="
                                          group
                                          flex items-center justify-between
                                          rounded-lg
                                          px-5 py-4
                                          text-xs font-semibold uppercase tracking-wide
                                          text-slate-700
                                          transition-all duration-300
                                          hover:bg-slate-50
                                          hover:text-blue-600
                                        "
                            >
                              <span className="flex items-center gap-2 transition-all duration-300 group-hover:translate-x-1">
                                <span className="h-1.5 w-1.5 rounded-full bg-slate-300 transition-colors duration-300 group-hover:bg-blue-600" />
                                {sub.name}
                              </span>
                            </Link>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              <NavItem
                title="IT Products"
                to="/it-products"
                menu="it-products"
                setOpenMenu={setOpenMenu}
                className={navItemClass}
              />

              <div
                className="relative shrink-0"
                onMouseEnter={() => {
                  setOpenMenu("ip-networks");
                  setSelectedCategory(0);
                }}
              >
                <button className={navItemClass}>
                  <span>IT Product Catalog</span>
                  <ChevronDown
                    size={14}
                    className={`shrink-0 transition-transform duration-200 ${
                      openMenu === "ip-networks" ? "rotate-180" : ""
                    }`}
                  />
                </button>
                {openMenu === "ip-networks" && (
                  <div
                    className="fixed left-0 top-30 w-screen mt-1 z-50"
                    onMouseEnter={() => setOpenMenu("ip-networks")}
                    onMouseLeave={() => setOpenMenu(null)}
                  >
                    <div className="bg-white border border-slate-100 shadow-lg flex">
                      <div className="w-100 bg-slate-100/90 border-r border-slate-200 max-h-[50vh] lg:max-h-[45vh] xl:max-h-[40vh] min-h-[50vh] lg:min-h-[45vh] xl:min-h-[40vh] overflow-y-auto scrollbar-none">
                        {ipNetworkMenuData.map((category, index) => (
                          <div
                            key={category?._id}
                            onMouseEnter={() => setSelectedCategory(index)}
                            className={`mx-2 my-1 rounded-lg flex items-center justify-between px-5 py-4 text-xs font-bold uppercase cursor-pointer transition-all duration-300 ${
                              selectedCategory === index
                                ? "bg-slate-100 text-slate-900 border-l-4 border-blue-600"
                                : "text-slate-600 hover:bg-slate-50 hover:text-slate-900"
                            }`}
                          >
                            <span className="flex-1 min-w-0 truncate">
                              {category?.name}
                            </span>
                            <ChevronRight
                              size={16}
                              className={`ml-3 transition-all duration-300 ${selectedCategory === index ? "text-blue-600 translate-x-1" : "text-slate-400"}`}
                            />
                          </div>
                        ))}
                      </div>

                      <div className="flex-1 px-5 py-4 max-h-[50vh] lg:max-h-[45vh] xl:max-h-[40vh] min-h-[50vh] lg:min-h-[45vh] xl:min-h-[40vh] overflow-y-auto scrollbar-none">
                        <div className="grid grid-cols-2 xl:grid-cols-4 gap-4">
                          {(
                            ipNetworkMenuData[selectedCategory] ||
                            ipNetworkMenuData[0]
                          )?.subCategories?.map((sub) => (
                            <Link
                              onClick={closeAllMenus}
                              key={sub._id}
                              to={`/it-products/sub-category/${sub.slug}`}
                              className="group flex items-center justify-between rounded-lg px-5 py-4 text-xs font-semibold uppercase tracking-wide text-slate-700 transition-all duration-300 hover:bg-slate-50 hover:text-blue-600"
                            >
                              <span className="flex items-center gap-2 transition-all duration-300 group-hover:translate-x-1">
                                <span className="h-1.5 w-1.5 rounded-full bg-slate-300 transition-colors duration-300 group-hover:bg-blue-600" />
                                {sub.name}
                              </span>
                            </Link>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              <div
                className="relative shrink-0"
                onMouseEnter={() => {
                  setOpenMenu("solutions");
                  setSelectedSolution(0);
                }}
              >
                <Link
                  to="/solutions"
                  onClick={closeAllMenus}
                  className={navItemClass}
                >
                  <span>Solutions</span>

                  <ChevronDown
                    size={14}
                    className={`shrink-0 transition-transform duration-200 ${
                      openMenu === "solutions" ? "rotate-180" : ""
                    }`}
                  />
                </Link>

                {openMenu === "solutions" && (
                  <div
                    className="fixed left-0 top-30 w-screen mt-1 z-50"
                    onMouseEnter={() => setOpenMenu("solutions")}
                    onMouseLeave={() => setOpenMenu(null)}
                  >
                    <div className="bg-white border border-slate-100 shadow-lg flex">
                      {/* LEFT SIDE - SOLUTIONS LIST */}
                      <div
                        className="
            w-[420px]
            bg-slate-100/90
            border-r
            border-slate-200
            max-h-[50vh]
            lg:max-h-[45vh]
            xl:max-h-[40vh]
            min-h-[50vh]
            lg:min-h-[45vh]
            xl:min-h-[40vh]
            overflow-y-auto
            scrollbar-none
          "
                      >
                        {solutionsMenuData.map((solution, index) => (
                          <Link
                            key={solution?._id || index}
                            to={`/solutions/${solution.slug}`}
                            onClick={closeAllMenus}
                            onMouseEnter={() => setSelectedSolution(index)}
                            className={`
                group
                mx-2
                my-1
                rounded-lg
                flex
                items-center
                gap-3
                px-3
                py-2
                text-xs
                font-bold
                uppercase
                cursor-pointer
                transition-all
                duration-300

                ${
                  selectedSolution === index
                    ? "bg-white text-blue-600 border-l-4 border-blue-600 shadow-sm"
                    : "text-slate-600 hover:bg-white hover:text-blue-600"
                }
              `}
                          >
                            <MenuThumbnail
                              key={
                                solution?.sectionImage || solution?.bannerImage
                              }
                              image={
                                solution?.sectionImage || solution?.bannerImage
                              }
                            />
                            <span className="min-w-0 flex-1 truncate">
                              {solution?.bannerTitle}
                            </span>

                            <ChevronRight
                              size={16}
                              className={`ml-3 shrink-0 transition-all duration-300 ${
                                selectedSolution === index
                                  ? "text-blue-600 translate-x-1"
                                  : "text-slate-400 group-hover:text-blue-600 group-hover:translate-x-1"
                              }`}
                            />
                          </Link>
                        ))}
                      </div>

                      {/* RIGHT SIDE - HOVER PREVIEW */}
                      <div
                        className="
            flex-1
            max-h-[50vh]
            lg:max-h-[45vh]
            xl:max-h-[40vh]
            min-h-[50vh]
            lg:min-h-[45vh]
            xl:min-h-[40vh]
            overflow-hidden
            bg-white
          "
                      >
                        {solutionsMenuData[selectedSolution] && (
                          <div className="h-full grid grid-cols-2">
                            {/* IMAGE */}
                            <div className="relative w-full h-full bg-slate-100 overflow-hidden">
                              {solutionsMenuData[selectedSolution]
                                ?.sectionImage ||
                              solutionsMenuData[selectedSolution]
                                ?.bannerImage ? (
                                <div className="relative h-full w-full bg-slate-100 overflow-hidden">
                                  {GetImageUrl(
                                    solutionsMenuData[selectedSolution]
                                      ?.sectionImage ||
                                      solutionsMenuData[selectedSolution]
                                        ?.bannerImage,
                                  ) ? (
                                    <img
                                      src={GetImageUrl(
                                        solutionsMenuData[selectedSolution]
                                          ?.sectionImage ||
                                          solutionsMenuData[selectedSolution]
                                            ?.bannerImage,
                                      )}
                                      alt={
                                        solutionsMenuData[selectedSolution]
                                          ?.sectionAlt ||
                                        solutionsMenuData[selectedSolution]
                                          ?.bannerAlt ||
                                        solutionsMenuData[selectedSolution]
                                          ?.bannerTitle ||
                                        "Solution"
                                      }
                                      className="
                                        absolute
                                        inset-0
                                        w-full
                                        h-full
                                        object-cover
                                        object-center
                                      "
                                      onError={(e) => {
                                        e.currentTarget.src =
                                          "https://placehold.co/700x450?text=No+Image";
                                      }}
                                    />
                                  ) : (
                                    <div className="absolute inset-0 flex items-center justify-center text-slate-400">
                                      <ImageOff size={50} />
                                    </div>
                                  )}
                                </div>
                              ) : (
                                <div className="w-full h-full flex items-center justify-center text-slate-400">
                                  <ImageOff size={50} />
                                </div>
                              )}

                              <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent" />
                            </div>

                            {/* CONTENT */}
                            <div className="p-8 flex flex-col justify-center">
                              <span className="text-xs font-bold uppercase tracking-widest text-blue-600">
                                Solution Preview
                              </span>

                              <h3 className="mt-3 text-2xl font-bold text-slate-900 leading-snug">
                                {
                                  solutionsMenuData[selectedSolution]
                                    ?.bannerTitle
                                }
                              </h3>

                              <p className="mt-4 text-sm text-slate-600 leading-7 line-clamp-5">
                                {solutionsMenuData[selectedSolution]
                                  ?.bannerSubtitle ||
                                  solutionsMenuData[selectedSolution]
                                    ?.shortDescription ||
                                  "Explore this professional security and IT solution from Suraksha 24X7."}
                              </p>

                              <Link
                                to={`/solutions/${solutionsMenuData[selectedSolution]?.slug}`}
                                onClick={closeAllMenus}
                                className="
                    mt-6
                    inline-flex
                    w-fit
                    items-center
                    gap-2
                    rounded-lg
                    bg-blue-600
                    px-5
                    py-3
                    text-sm
                    font-semibold
                    text-white
                    transition-all
                    duration-300
                    hover:bg-blue-700
                  "
                              >
                                View Solution
                                <ArrowRight size={16} />
                              </Link>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>

              <div
                className="relative shrink-0"
                onMouseEnter={() => {
                  setOpenMenu("services");
                  setSelectedService(0);
                }}
              >
                <Link
                  to="/services"
                  onClick={closeAllMenus}
                  className={navItemClass}
                >
                  <span>Services</span>

                  <ChevronDown
                    size={14}
                    className={`shrink-0 transition-transform duration-200 ${
                      openMenu === "services" ? "rotate-180" : ""
                    }`}
                  />
                </Link>

                {openMenu === "services" && (
                  <div
                    className="fixed left-0 top-30 w-screen mt-1 z-50"
                    onMouseEnter={() => setOpenMenu("services")}
                    onMouseLeave={() => setOpenMenu(null)}
                  >
                    <div className="bg-white border border-slate-100 shadow-lg flex">
                      {/* LEFT SIDE - SERVICES */}
                      <div
                        className="
            w-[420px]
            bg-slate-100/90
            border-r
            border-slate-200
            max-h-[50vh]
            lg:max-h-[45vh]
            xl:max-h-[40vh]
            min-h-[50vh]
            lg:min-h-[45vh]
            xl:min-h-[40vh]
            overflow-y-auto
            scrollbar-none
          "
                      >
                        {servicesMenuData.map((service, index) => (
                          <Link
                            key={service?._id || index}
                            to={`/services/${service.slug}`}
                            onClick={closeAllMenus}
                            onMouseEnter={() => setSelectedService(index)}
                            className={`
                group
                mx-2
                my-1
                rounded-lg
                flex
                items-center
                gap-3
                px-3
                py-2
                text-xs
                font-bold
                uppercase
                cursor-pointer
                transition-all
                duration-300

                ${
                  selectedService === index
                    ? "bg-white text-blue-600 border-l-4 border-blue-600 shadow-sm"
                    : "text-slate-600 hover:bg-white hover:text-blue-600"
                }
              `}
                          >
                            <MenuThumbnail
                              key={
                                service?.sectionImage || service?.bannerImage
                              }
                              image={
                                service?.sectionImage || service?.bannerImage
                              }
                            />
                            <span className="min-w-0 flex-1 truncate">
                              {service?.bannerTitle}
                            </span>

                            <ChevronRight
                              size={16}
                              className={`ml-3 shrink-0 transition-all duration-300 ${
                                selectedService === index
                                  ? "text-blue-600 translate-x-1"
                                  : "text-slate-400 group-hover:text-blue-600 group-hover:translate-x-1"
                              }`}
                            />
                          </Link>
                        ))}
                      </div>

                      {/* RIGHT SIDE - HOVER PREVIEW */}
                      <div
                        className="
            flex-1
            max-h-[50vh]
            lg:max-h-[45vh]
            xl:max-h-[40vh]
            min-h-[50vh]
            lg:min-h-[45vh]
            xl:min-h-[40vh]
            overflow-hidden
            bg-white
          "
                      >
                        {servicesMenuData[selectedService] && (
                          <div className="h-full grid grid-cols-2">
                            {/* PREVIEW IMAGE */}
                            <div className="relative h-full w-full bg-slate-100 overflow-hidden">
                              {servicesMenuData[selectedService]
                                ?.sectionImage ||
                              servicesMenuData[selectedService]?.bannerImage ? (
                                <img
                                  src={GetImageUrl(
                                    servicesMenuData[selectedService]
                                      ?.sectionImage ||
                                      servicesMenuData[selectedService]
                                        ?.bannerImage,
                                  )}
                                  alt={
                                    servicesMenuData[selectedService]
                                      ?.sectionAlt ||
                                    servicesMenuData[selectedService]
                                      ?.bannerAlt ||
                                    servicesMenuData[selectedService]
                                      ?.bannerTitle ||
                                    "Service"
                                  }
                                  className="
                      w-full
                      h-full
                      object-cover
                      object-center
                      transition-transform
                      duration-500
                      hover:scale-105
                    "
                                />
                              ) : (
                                <div className="w-full h-full flex items-center justify-center text-slate-400">
                                  <ImageOff size={50} />
                                </div>
                              )}

                              {/* Gradient */}
                              <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent" />
                            </div>

                            {/* PREVIEW CONTENT */}
                            <div className="p-8 flex flex-col justify-center">
                              <span className="text-xs font-bold uppercase tracking-widest text-blue-600">
                                Service Preview
                              </span>

                              <h3 className="mt-3 text-2xl font-bold text-slate-900 leading-snug">
                                {servicesMenuData[selectedService]?.bannerTitle}
                              </h3>

                              <p className="mt-4 text-sm text-slate-600 leading-7 line-clamp-5">
                                {servicesMenuData[selectedService]
                                  ?.bannerSubtitle ||
                                  servicesMenuData[selectedService]
                                    ?.shortDescription ||
                                  "Explore this professional security and IT service from Suraksha 24X7."}
                              </p>

                              <Link
                                to={`/services/${servicesMenuData[selectedService]?.slug}`}
                                onClick={closeAllMenus}
                                className="
                    mt-6
                    inline-flex
                    w-fit
                    items-center
                    gap-2
                    rounded-lg
                    bg-blue-600
                    px-5
                    py-3
                    text-sm
                    font-semibold
                    text-white
                    transition-all
                    duration-300
                    hover:bg-blue-700
                  "
                              >
                                View Service
                                <ArrowRight size={16} />
                              </Link>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>

              <div
                className="relative shrink-0"
                onMouseEnter={() => {
                  setOpenMenu("resources");
                }}
              >
                <button className={navItemClass}>
                  <span>Resources</span>
                  <ChevronDown
                    size={14}
                    className={`shrink-0 transition-transform duration-200 ${
                      openMenu === "resources" ? "rotate-180" : ""
                    }`}
                  />
                </button>
                {openMenu === "resources" && (
                  <div
                    className="fixed left-0 top-30 w-screen mt-1 z-50 "
                    onMouseEnter={() => setOpenMenu("resources")}
                    onMouseLeave={() => setOpenMenu(null)}
                  >
                    <div className="bg-white border border-slate-100 shadow-lg flex">
                      <div className="flex-1 px-5 py-10 max-h-[40vh] overflow-y-auto scrollbar-none">
                        {/* <div className="grid  grid-cols-4 gap-6"> */}
                        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6">
                          {resourceItems.map((item) => (
                            <Link
                              key={item.title}
                              to={item.path}
                              onClick={closeAllMenus}
                              className="
    group relative overflow-hidden
    flex flex-col items-center justify-center
    text-center
    h-36
    rounded-xl
    border border-slate-200
    bg-white
    transition-all duration-300
    hover:-translate-y-1
    hover:border-blue-500
    hover:shadow-lg
    
  "
                            >
                              <div className="absolute -top-8 -right-8 w-20 h-20 rounded-full bg-blue-100 opacity-0 group-hover:opacity-40 group-hover:scale-125 transition-all duration-500" />

                              <div
                                className={`
      relative z-10
      w-14 h-14
      rounded-xl
      flex items-center justify-center
      ${item.color}
      transition-all duration-300
      group-hover:scale-110
      group-hover:-translate-y-1
    `}
                              >
                                {React.cloneElement(item.icon, { size: 26 })}
                              </div>

                              <h3 className="relative z-10 mt-4 text-sm font-bold text-slate-900 group-hover:text-blue-600 transition-colors">
                                {item.title}
                              </h3>
                            </Link>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </nav>

            <div className="flex items-center gap-3">
              <Link
                to="/login"
                className="hidden min-[1280px]:flex items-center gap-2 bg-slate-100 hover:bg-blue-600 hover:text-white transition px-4 py-2 rounded-md"
              >
                <User size={18} />
                <span className="text-sm font-semibold uppercase">Login</span>
              </Link>

              <Link
                to="/login"
                className="min-[1280px]:hidden w-10 h-10 rounded-md bg-slate-100 flex items-center justify-center"
              >
                <User size={18} />
              </Link>

              <button
                onClick={() => setMobileOpen(true)}
                className="min-[900px]:hidden w-10 h-10 rounded-md bg-slate-100 flex items-center justify-center"
              >
                <Menu size={22} />
              </button>
            </div>
          </div>
        </div>
      </header>

      {mobileOpen && (
        <>
          <div
            className="fixed inset-0  z-40"
            onClick={() => closeMobileMenu()}
          />

          <aside className="fixed right-0 top-[140px] h-[calc(100vh-140px)] md:top-[170px] w-full bg-white z-50 min-[900px]:hidden flex flex-col py-10">
            <div className="relative h-14 flex items-center justify-end px-4 flex-shrink-0">
              <button
                onClick={() => closeMobileMenu()}
                className="w-7 h-7 flex items-center justify-center rounded-full text-slate-500 hover:text-slate-900 transition"
              >
                <X size={18} strokeWidth={2} />
              </button>
            </div>

            <nav className="flex-1 overflow-y-auto px-5 pb-6">
              <Link
                to="/"
                onClick={() => closeMobileMenu()}
                className="block py-3 uppercase text-sm font-semibold text-slate-700"
              >
                Home
              </Link>

              <Link
                to="/about"
                onClick={() => closeMobileMenu()}
                className="block py-3 uppercase text-sm font-semibold text-slate-700"
              >
                About
              </Link>

              <Link
                to="/security-product"
                onClick={() => closeMobileMenu()}
                className="block py-3 uppercase text-sm font-semibold text-slate-700"
              >
                Security Products
              </Link>

              <div>
                <button
                  onClick={() =>
                    setMobileSection(
                      mobileSection === "catalog" ? null : "catalog",
                    )
                  }
                  className="
      w-full flex justify-between items-center
      py-2.5
      uppercase
     text-sm
      font-semibold
      text-slate-700
    "
                >
                  <span>Security Product Catalog</span>

                  <ChevronDown
                    size={14}
                    className={`transition-transform duration-200 ${
                      mobileSection === "catalog" ? "rotate-180" : ""
                    }`}
                  />
                </button>

                {mobileSection === "catalog" && (
                  <div className="pl-2 pb-2 space-y-2">
                    {catalogMenuData.map((category, index) => (
                      <div key={category._id}>
                        <button
                          onClick={() =>
                            setMobileCategory(
                              mobileCategory === index ? null : index,
                            )
                          }
                          className="
              w-full
              flex
              items-center
              justify-between
              py-2
              text-xs sm:text-sm
              font-medium
              text-slate-600
            "
                        >
                          <span className="flex items-center gap-2">
                            <span className="w-1.5 h-1.5 rounded-full bg-slate-400" />
                            <span className="truncate max-w-[220px] md:max-w-[400px]">
                              {category.name}
                            </span>
                          </span>

                          <ChevronRight
                            size={13}
                            className={`flex-shrink-0 transition-transform duration-200 ${mobileCategory === index ? "rotate-90 text-blue-600" : "text-slate-400"}`}
                          />
                        </button>

                        {mobileCategory === index && (
                          <div className="ml-5 mt-1 space-y-1.5 border-l border-slate-200 pl-3">
                            {category.subCategories?.map((sub) => (
                              <Link
                                key={sub._id}
                                to={`/security-product/sub-category/${sub.slug}`}
                                onClick={() => closeMobileMenu()}
                                className="flex items-center gap-2 py-1 text-xs text-slate-500"
                              >
                                <span className="truncate">{sub.name}</span>
                              </Link>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <Link
                to="/it-products"
                onClick={() => closeMobileMenu()}
                className="block py-3 uppercase text-sm font-semibold text-slate-700"
              >
                IT Products
              </Link>

              <div>
                <button
                  onClick={() =>
                    setMobileSection(
                      mobileSection === "ip-networks" ? null : "ip-networks",
                    )
                  }
                  className="w-full flex justify-between items-center py-2.5 uppercase text-sm font-semibold text-slate-700"
                >
                  <span className="uppercase">IT Product Catalog</span>
                  <ChevronDown
                    size={14}
                    className={`uppercase transition-transform duration-200 ${mobileSection === "ip-networks" ? "rotate-180" : ""}`}
                  />
                </button>

                {mobileSection === "ip-networks" && (
                  <div className="uppercase pl-2 pb-2 space-y-2">
                    {ipNetworkMenuData.map((category, index) => (
                      <div key={category._id}>
                        <button
                          onClick={() =>
                            setMobileCategory(
                              mobileCategory === `ip-${index}`
                                ? null
                                : `ip-${index}`,
                            )
                          }
                          className="uppercase w-full flex items-center justify-between py-2 text-xs sm:text-sm font-medium text-slate-600"
                        >
                          <span className="flex items-center gap-2">
                            <span className="w-1.5 h-1.5 rounded-full bg-slate-400" />
                            <span className="uppercase truncate max-w-[220px] md:max-w-[400px]">
                              {category.name}
                            </span>
                          </span>
                          <ChevronRight
                            size={13}
                            className={`uppercase flex-shrink-0 transition-transform duration-200 ${mobileCategory === `ip-${index}` ? "rotate-90 text-blue-600" : "text-slate-400"}`}
                          />
                        </button>

                        {mobileCategory === `ip-${index}` && (
                          <div className="uppercase ml-5 mt-1 space-y-1.5 border-l border-slate-200 pl-3">
                            {category.subCategories?.map((sub) => (
                              <Link
                                key={sub._id}
                                to={`/it-products/category/${category.slug}`}
                                onClick={() => closeMobileMenu()}
                                className="uppercase flex items-center gap-2 py-1 text-xs text-slate-500"
                              >
                                <span className="truncate">{sub.name}</span>
                              </Link>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="">
                <div className="w-full flex justify-between py-3 uppercase text-sm font-semibold text-slate-700">
                  <Link
                    to="/solutions"
                    onClick={() => closeMobileMenu()}
                    className="flex-1"
                  >
                    Solutions
                  </Link>

                  <button
                    onClick={() =>
                      setMobileSection(
                        mobileSection === "solutions" ? null : "solutions",
                      )
                    }
                    className="flex items-center"
                    aria-label="Toggle solutions menu"
                  >
                    <ChevronDown
                      size={16}
                      className={`transition-transform ${
                        mobileSection === "solutions" ? "rotate-180" : ""
                      }`}
                    />
                  </button>
                </div>

                {mobileSection === "solutions" && (
                  <div className="pl-2 pb-3 space-y-3">
                    {solutionsMenuData.map((item) => (
                      <Link
                        key={item._id}
                        to={`/solutions/${item.slug}`}
                        onClick={() => closeMobileMenu()}
                        className="uppercase flex min-w-0 items-center gap-3 rounded-md px-2 py-2 text-sm text-slate-700 transition-colors hover:bg-slate-50"
                      >
                        <MenuThumbnail
                          image={item?.sectionImage || item?.bannerImage}
                          size="mobile"
                        />

                        <span className="min-w-0 flex-1">
                          {item.bannerTitle}
                        </span>
                      </Link>
                    ))}
                  </div>
                )}
              </div>

              <div className="">
                <div className="w-full flex justify-between py-3 uppercase text-sm font-semibold text-slate-700">
                  <Link
                    to="/services"
                    onClick={() => closeMobileMenu()}
                    className="flex-1"
                  >
                    Services
                  </Link>

                  <button
                    onClick={() =>
                      setMobileSection(
                        mobileSection === "services" ? null : "services",
                      )
                    }
                    className="flex items-center"
                    aria-label="Toggle services menu"
                  >
                    <ChevronDown
                      size={16}
                      className={`transition-transform ${
                        mobileSection === "services" ? "rotate-180" : ""
                      }`}
                    />
                  </button>
                </div>

                {mobileSection === "services" && (
                  <div className="pl-2 pb-3 space-y-3">
                    {servicesMenuData.map((item) => (
                      <Link
                        key={item._id}
                        to={`/services/${item.slug}`}
                        onClick={() => closeMobileMenu()}
                        className="uppercase flex min-w-0 items-center gap-3 rounded-md px-2 py-2 text-sm text-slate-700 transition-colors hover:bg-slate-50"
                      >
                        <MenuThumbnail
                          image={item?.sectionImage || item?.bannerImage}
                          size="mobile"
                        />

                        <span className="min-w-0 flex-1">
                          {item.bannerTitle}
                        </span>
                      </Link>
                    ))}
                  </div>
                )}
              </div>

              <div>
                <button
                  onClick={() =>
                    setMobileSection(
                      mobileSection === "resources" ? null : "resources",
                    )
                  }
                  className="w-full flex justify-between py-3  uppercase text-sm font-semibold text-slate-700"
                >
                  Resources
                  <ChevronDown
                    size={16}
                    className={
                      mobileSection === "resources" ? "rotate-180" : ""
                    }
                  />
                </button>

                {mobileSection === "resources" && (
                  <div className="pl-2 pb-3 space-y-3">
                    {resourceItems.map((item) => (
                      <Link
                        key={item.title}
                        to={item.path}
                        onClick={() => closeMobileMenu()}
                        className="uppercase 
flex items-center gap-2
text-sm text-slate-700
"
                      >
                        <span className="w-1.5 h-1.5 rounded-full bg-slate-400" />

                        {item.title}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            </nav>
          </aside>
        </>
      )}
    </>
  );
}

export default Head;
